import type { FormSchemaEntityType } from '@mygmao/shared';
import { Lock } from 'lucide-react';

/**
 * Référence des **champs de base** (codés en dur dans les modals CRUD) par
 * entité. Affiché en lecture seule dans la prévisualisation du Form Builder
 * (`/admin/formulaires`) pour donner au designer une vue d'ensemble : il sait
 * d'emblée ce qui existe déjà et n'a donc PAS besoin de l'ajouter au schéma.
 *
 * Source de vérité : les `<XFormModal>` correspondants côté frontend.
 */

interface BaseField {
  label: string;
  type: 'text' | 'number' | 'select' | 'boolean' | 'date' | 'email' | 'textarea';
  /** Valeurs énumérées si type=select. */
  options?: string[];
  /** Champ obligatoire dans le modal de base. */
  required?: boolean;
  hint?: string;
}

const BASE_FIELDS_BY_ENTITY: Partial<Record<FormSchemaEntityType, BaseField[]>> = {
  user: [
    { label: 'Email', type: 'email', required: true },
    { label: 'Prénom', type: 'text', required: true },
    { label: 'Nom', type: 'text', required: true },
    { label: 'Rôle', type: 'select', required: true, hint: 'rôles tenant disponibles' },
    { label: 'Service', type: 'select', hint: 'services actifs du tenant' },
    { label: 'Actif', type: 'boolean' },
  ],
  batiment: [
    { label: 'Code', type: 'text', required: true, hint: 'unique par tenant — auto-suggéré' },
    { label: 'Nom', type: 'text', required: true },
    {
      label: 'Type',
      type: 'select',
      required: true,
      options: ['principal', 'annexe', 'technique', 'administratif', 'operationnel'],
    },
    {
      label: 'Statut',
      type: 'select',
      required: true,
      options: ['en_service', 'en_travaux', 'ferme_temporairement', 'en_projet', 'desaffecte'],
    },
    { label: 'Adresse', type: 'textarea' },
    { label: 'Surface totale (m²)', type: 'number' },
    { label: "Nombre d'étages", type: 'number' },
    { label: 'Responsable', type: 'select', hint: 'utilisateurs du tenant' },
  ],
  etage: [
    { label: 'Bâtiment parent', type: 'select', required: true },
    { label: 'Code', type: 'text', required: true },
    { label: 'Numéro', type: 'text', required: true, hint: 'ex. RDC, 1, 2, -1' },
    { label: 'Nom', type: 'text' },
    { label: 'Surface totale (m²)', type: 'number' },
    {
      label: 'Statut',
      type: 'select',
      required: true,
      options: ['operationnel', 'acces_restreint', 'en_renovation', 'ferme', 'acces_interdit'],
    },
    { label: 'Responsable', type: 'select' },
  ],
  salle: [
    { label: 'Étage parent', type: 'select', required: true },
    { label: 'Code', type: 'text', required: true },
    { label: 'Numéro porte', type: 'text' },
    { label: 'Nom', type: 'text', required: true },
    {
      label: 'Type',
      type: 'select',
      required: true,
      options: ['accueil', 'activite', 'technique', 'bureau', 'stockage', 'circulation', 'autre'],
    },
    { label: 'Surface (m²)', type: 'number' },
    {
      label: 'Statut',
      type: 'select',
      required: true,
      options: [
        'disponible',
        'occupee',
        'en_maintenance',
        'en_nettoyage',
        'hors_service',
        'reservee',
        'en_travaux',
      ],
    },
    { label: 'Service affecté', type: 'select' },
    { label: 'Responsable', type: 'select' },
    { label: 'Réservable', type: 'boolean' },
    { label: 'Durée max réservation (min)', type: 'number' },
    { label: 'Préavis requis (h)', type: 'number' },
    { label: 'Validation requise', type: 'boolean' },
  ],
  service: [
    { label: 'Code', type: 'text', required: true, hint: 'unique par tenant — auto-suggéré' },
    { label: 'Nom complet', type: 'text', required: true },
    {
      label: 'Type de service',
      type: 'select',
      required: true,
      options: ['operationnel', 'technique', 'logistique', 'support', 'administratif'],
    },
    {
      label: 'Statut',
      type: 'select',
      required: true,
      options: ['actif', 'inactif', 'en_restructuration', 'ferme_temporairement', 'desaffecte'],
    },
    { label: 'Service parent', type: 'select', hint: 'hiérarchie organisationnelle' },
    { label: 'Capacité max simultanée', type: 'number' },
    { label: 'Nombre postes travail', type: 'number' },
    { label: 'Surface totale (m²)', type: 'number' },
    { label: 'Autorisation déplacement équipement', type: 'boolean' },
    { label: 'Validation requise déplacement', type: 'boolean' },
  ],
  equipement: [
    { label: 'Code équipement', type: 'text', required: true, hint: 'auto-suggéré' },
    { label: 'Numéro inventaire', type: 'text' },
    { label: 'Code-barres', type: 'text' },
    { label: 'Numéro de série', type: 'text' },
    { label: 'Désignation', type: 'text', required: true },
    {
      label: 'Type',
      type: 'select',
      required: true,
      options: ['production', 'technique', 'informatique', 'mobilier'],
    },
    {
      label: 'Statut',
      type: 'select',
      required: true,
      options: ['en_service', 'en_panne', 'en_maintenance', 'reforme', 'en_stock'],
    },
    {
      label: 'Criticité',
      type: 'select',
      options: ['vital', 'critique', 'important', 'standard'],
    },
    { label: 'Fabricant', type: 'text' },
    { label: 'Modèle', type: 'text' },
    { label: 'Service', type: 'select' },
    { label: 'Localisation (bât. / étage / salle)', type: 'select' },
    { label: 'Responsable', type: 'select' },
    { label: 'Est mobile', type: 'boolean' },
  ],
  intervention: [
    { label: 'Titre', type: 'text', required: true },
    { label: 'Description', type: 'textarea', required: true },
    {
      label: 'Type de demande',
      type: 'select',
      required: true,
      options: [
        'panne',
        'urgence_critique',
        'preventive',
        'amelioration',
        'installation',
        'demenagement',
        'controle_reglementaire',
        'expertise',
      ],
    },
    {
      label: 'Priorité',
      type: 'select',
      required: true,
      options: ['P0', 'P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7'],
    },
    { label: 'Service demandeur', type: 'select' },
    { label: 'Équipement concerné', type: 'select' },
    { label: 'Date planifiée', type: 'date' },
    { label: 'Durée estimée (min)', type: 'number' },
  ],
};

const TYPE_LABEL: Record<BaseField['type'], string> = {
  text: 'texte',
  number: 'nombre',
  select: 'liste',
  boolean: 'oui/non',
  date: 'date',
  email: 'email',
  textarea: 'texte long',
};

export function BaseFieldsPreview({ entity }: { entity: FormSchemaEntityType }) {
  const fields = BASE_FIELDS_BY_ENTITY[entity] ?? [];
  if (fields.length === 0) return null;

  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-center mb-2">
        <Lock className="w-3.5 h-3.5 text-gray-500 mr-1.5" />
        <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
          Champs de base (lecture seule)
        </h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Ces champs sont déjà présents dans le formulaire de l'entité{' '}
        <code className="px-1 bg-white rounded">{entity}</code>. Inutile de les ajouter au schéma —
        ils seront automatiquement rendus avant vos champs custom.
      </p>
      <ul className="space-y-1">
        {fields.map((f) => (
          <li key={f.label} className="flex items-baseline gap-2 text-xs">
            <span className="font-medium text-gray-800">{f.label}</span>
            {f.required && (
              <span className="text-red-500 font-bold" title="Obligatoire">
                *
              </span>
            )}
            <span className="px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-600">
              {TYPE_LABEL[f.type]}
            </span>
            {f.options && (
              <span className="text-gray-400 truncate" title={f.options.join(' / ')}>
                ({f.options.join(' / ')})
              </span>
            )}
            {f.hint && <span className="text-gray-400 italic">— {f.hint}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
